import {
  ApostleFilterInput,
  ApostleLite,
  ApostleStatus
} from '@apeiron/apostle'
import {
  ApeironBlueButton,
  INFO_POPUP_TYPE,
  InfoPopup,
  useMessageContext
} from '@apeiron/temp'
import { ApeironCheckbox } from '@apeiron/ui'
import { Box, styled, Typography } from '@mui/material'
import ClaimBountyPriceTable from '@src/components/apostles/bounty/ClaimBountyPriceTable'
import ApostleCardOverlay from '@src/components/apostles/card/ApostleCardOverlay'
import MarketplaceApostleCard from '@src/components/apostles/card/MarketplaceApostleCard'
import ApostleMultiSelectionSlots, {
  RenderPopupCardProps
} from '@src/components/apostles/share/multi-select/ApostleMultiSelectionSlots'
import ApostleNotQualifiedOverlay from '@src/components/apostles/share/multi-select/ApostleNotQualifiedOverlay'
import ApostleSelectedOverlay from '@src/components/apostles/share/multi-select/ApostleSelectedOverlay'
import { useBountyContext } from '@src/contexts/share/BountyContext'
import useBounty from '@src/hooks/contract/useBounty'
import { BountyNode } from '@src/types/bounty'
import { toApostleClassType } from '@src/util/apeiron/apostle'
import * as R from 'ramda'
import { FC, useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

const Popup = styled(InfoPopup)`
  max-height: 98%;
  max-width: 98%;
  width: 720px;

  .Custom-Content {
    align-items: center;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    padding-bottom: 40px;
  }
`

const Title = styled(Typography)`
  text-align: center;
  ${props => props.theme.breakpoints.up('res768')} {
    font-size: 20px;
  }
  ${props => props.theme.breakpoints.down('res768')} {
    font-size: 16px;
    font-weight: 300;
    padding: 5px 10px;
  }
`

const Container = styled(Box)`
  width: 700px;
  max-width: unset;
`

const AgreementCheckbox = styled(ApeironCheckbox)`
  .Custom-Description {
    font-size: 12px;
  }
`

const BottomContainer = styled(Box)`
  max-width: 400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const SubmitButton = styled(ApeironBlueButton)`
  width: 260px;
  margin-top: 20px;
`

const RedText = styled(Typography)`
  color: #ff0000;
`

const StyledApostleMultiSelectionSlots = styled(ApostleMultiSelectionSlots)`
  padding-top: 15px;
  .Selection-Slot .Apostle-Card {
    width: 140px;

    ${props => props.theme.breakpoints.down('res768')} {
      width: 120px;
    }
  }
`

const checkIsQualified = (apostle: ApostleLite, bounty: BountyNode) => {
  if (!apostle) {
    return false
  }

  const firstSkillFulfilled =
    !bounty.bountyRequired.mainSkillFullID ||
    (apostle.skillExps?.[0]?.key === bounty.bountyRequired.mainSkillFullID &&
      apostle.skillExps?.[0]?.level >= bounty.bountyRequired.minMainSkillLevel)

  const otherSkill2Fulfilled =
    !bounty.bountyRequired.otherSkillFullID1 ||
    apostle.skillExps.some(
      (exp, index) =>
        index > 0 &&
        exp.key === bounty.bountyRequired.otherSkillFullID1 &&
        exp.level >= bounty.bountyRequired.minOtherSkill1Level
    )

  const otherSkill3Fulfilled =
    !bounty.bountyRequired.otherSkillFullID2 ||
    apostle.skillExps.some(
      (exp, index) =>
        index > 0 &&
        exp.key === bounty.bountyRequired.otherSkillFullID2 &&
        exp.level >= bounty.bountyRequired.minOtherSkill2Level
    )

  return (
    apostle.iv < bounty.bountyRequired.minIV ||
    apostle.class.type !== toApostleClassType(bounty.bountyRequired.classID) ||
    !firstSkillFulfilled ||
    !otherSkill2Fulfilled ||
    !otherSkill3Fulfilled
  )
}

const createFilterInputFromBounty = (
  bounty: BountyNode
): ApostleFilterInput => {
  const otherSkillIds = []

  if (bounty?.bountyRequired.otherSkillFullID1) {
    otherSkillIds.push(bounty.bountyRequired.otherSkillFullID1)
  }

  if (bounty?.bountyRequired.otherSkillFullID2) {
    otherSkillIds.push(bounty.bountyRequired.otherSkillFullID2)
  }

  const classType = toApostleClassType(bounty?.bountyRequired.classID)

  const lowerestSkillLevel = Math.min(
    bounty?.bountyRequired.minMainSkillLevel,
    bounty?.bountyRequired.minOtherSkill1Level,
    bounty?.bountyRequired.minOtherSkill2Level
  )

  const filterInput: ApostleFilterInput = {
    ivMin: bounty?.bountyRequired.minIV,
    skillLevelMin: lowerestSkillLevel,
    firstSkillIDs: bounty?.bountyRequired.mainSkillFullID
      ? [bounty?.bountyRequired.mainSkillFullID]
      : [],
    otherSkillIDs: otherSkillIds,
    class: classType ? [classType] : undefined
  }

  return filterInput
}

const Content: FC<Props> = props => {
  const { preselect = [], onClaimSuccess } = props

  const { setSnackInfo } = useMessageContext()

  const { claimingBounty, setClaimingBounty, refetchBounties } =
    useBountyContext()

  const { claimBounty } = useBounty()

  const [isAgreed, setIsAgreed] = useState(false)

  const [selectedApostles, setSelectedApostles] =
    useState<ApostleLite[]>(preselect)

  const { t } = useTranslation()

  const handleClose = useCallback(() => {
    setClaimingBounty(null)
  }, [setClaimingBounty])

  const handleOnCheckBoxChange = useCallback(() => {
    setIsAgreed(!isAgreed)
  }, [isAgreed])

  const handleClaimBounty = useCallback(async () => {
    if (!claimingBounty) {
      return
    }

    const success = await claimBounty({
      bountyId: claimingBounty.bountyID.toString(),
      apostleIds: selectedApostles.map(apostle => apostle.id)
    })

    if (success) {
      if (onClaimSuccess) {
        onClaimSuccess(claimingBounty)
      }

      setSnackInfo({
        message: t('contract.bounty.claim.success'),
        serverity: 'success'
      })
      handleClose()
      refetchBounties()
    }
  }, [
    claimingBounty,
    selectedApostles,
    claimBounty,
    onClaimSuccess,
    setSnackInfo,
    t,
    handleClose,
    refetchBounties
  ])

  const defaultFilterInput = useMemo(() => {
    if (!claimingBounty) {
      return undefined
    }

    return createFilterInputFromBounty(claimingBounty)
  }, [claimingBounty])

  const renderSelectPopupCard = useCallback(
    ({ apostle, isSelected, onClick }: RenderPopupCardProps) => {
      if (!claimingBounty) {
        return null
      }

      const isNotQualified = checkIsQualified(apostle, claimingBounty)

      const notAvailable = apostle.status !== ApostleStatus.Enable

      return (
        <MarketplaceApostleCard
          apostle={apostle}
          disabled={isNotQualified || notAvailable}
          onClick={onClick}
          dimmed={isNotQualified || isSelected}
          outerOverlay={
            isNotQualified ? (
              <ApostleNotQualifiedOverlay />
            ) : notAvailable ? (
              <ApostleCardOverlay>{apostle.status}</ApostleCardOverlay>
            ) : (
              isSelected && <ApostleSelectedOverlay />
            )
          }
        />
      )
    },
    [claimingBounty]
  )

  return claimingBounty ? (
    <>
      <Title>{t('bounty.apostle.claim.header')}</Title>
      <Container>
        <StyledApostleMultiSelectionSlots
          slotCount={claimingBounty.remainingBountyNumber}
          selectedApostles={selectedApostles}
          setSelectedApostles={setSelectedApostles}
          defaultFilterInput={defaultFilterInput}
          renderPopupCard={renderSelectPopupCard}
        />
        <BottomContainer>
          <ClaimBountyPriceTable
            unitPrice={claimingBounty.unitPrice}
            quantity={selectedApostles.length}
            currencyType={claimingBounty.currencyType}
          />
          <AgreementCheckbox
            checked={isAgreed}
            description={
              <Trans
                components={{
                  warning: <RedText as={Box} />
                }}
              >
                {t('bounty.apostle.claim.acknowledgement')}
              </Trans>
            }
            onChange={handleOnCheckBoxChange}
          />
          <SubmitButton
            disabled={!isAgreed || selectedApostles.length === 0}
            onClick={handleClaimBounty}
          >
            {t('bounty.apostle.claim.complete_bounty')}
          </SubmitButton>
        </BottomContainer>
      </Container>
    </>
  ) : (
    <></>
  )
}

const ClaimApostleBountyDialog: FC<Props> = props => {
  const { preselect, onClaimSuccess } = props

  const { claimingBounty, setClaimingBounty } = useBountyContext()

  const handleClose = useCallback(() => {
    setClaimingBounty(null)
  }, [setClaimingBounty])

  return (
    <Popup
      type={INFO_POPUP_TYPE.BLACK}
      open={R.isNotNil(claimingBounty)}
      onClose={handleClose}
    >
      <Content preselect={preselect} onClaimSuccess={onClaimSuccess} />
    </Popup>
  )
}

type Props = {
  preselect?: ApostleLite[]
  onClaimSuccess?: (bounty: BountyNode) => void
}

export default ClaimApostleBountyDialog
